import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';

interface LegalPageProps {
  pageKey: 'privacy' | 'terms' | 'refund';
  fallbackTitle: string;
  fetcher: () => Promise<{ title: string; content: string }>;
  description: string;
  icon: string;
  accentColor: string;
  lastUpdated?: string;
}

const PAGES: Record<LegalPageProps['pageKey'], { path: string; label: string; icon: string; accent: string }> = {
  privacy: { path: '/privacy-policy', label: 'Privacy Policy', icon: '🔒', accent: 'text-blue-600 bg-blue-50 border-blue-100' },
  terms: { path: '/terms', label: 'Terms & Conditions', icon: '📜', accent: 'text-purple-600 bg-purple-50 border-purple-100' },
  refund: { path: '/refund-policy', label: 'Refund Policy', icon: '💸', accent: 'text-green-600 bg-green-50 border-green-100' },
};

const LegalPage: React.FC<LegalPageProps> = ({ pageKey, fallbackTitle, fetcher, description, icon, accentColor, lastUpdated = 'May 16, 2025' }) => {
  const [data, setData] = useState<{ title: string; content: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [readProgress, setReadProgress] = useState(0);
  const [activeSection, setActiveSection] = useState<string>('');
  const contentRef = useRef<HTMLDivElement>(null);

  const load = async () => {
    try {
      setIsLoading(true);
      const result = await fetcher();
      setData(result);
      setError(null);
    } catch {
      setError(`Failed to load ${fallbackTitle}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Reading progress bar
  useEffect(() => {
    const handleScroll = () => {
      const el = contentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      const scrolled = Math.max(0, -rect.top);
      const pct = total > 0 ? Math.min(100, (scrolled / total) * 100) : 0;
      setReadProgress(pct);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [data]);

  // Build table of contents from markdown headings (h2)
  const sections = React.useMemo(() => {
    if (!data?.content) return [] as { id: string; label: string }[];
    const lines = data.content.split('\n');
    const result: { id: string; label: string }[] = [];
    lines.forEach((line) => {
      const m = line.match(/^##\s+(.+)$/);
      if (m) {
        const label = m[1].trim();
        const id = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        result.push({ id, label });
      }
    });
    return result;
  }, [data?.content]);

  // Track active section on scroll
  useEffect(() => {
    if (sections.length === 0) return;
    const handleScroll = () => {
      let current = sections[0]?.id || '';
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= 150) current = s.id;
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const title = data?.title || fallbackTitle;

  return (
    <div className="bg-gray-50 min-h-screen">
      <Helmet>
        <title>{title} — EatMeat</title>
        <meta name="description" content={description} />
      </Helmet>

      <style>{`
        @keyframes pulseBlob { 0%,100% { opacity: 0.2; } 50% { opacity: 0.4; } }
        @keyframes fadeSlide { 0% { opacity: 0; transform: translateY(12px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes progressFill { 0% { width: 0; } 100% { width: var(--w); } }
      `}</style>

      {/* Reading progress bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-gray-100 z-40">
        <div
          className="h-full bg-gradient-to-r from-primary to-orange-500 transition-[width] duration-150"
          style={{ width: `${readProgress}%` }}
        />
      </div>

      {/* Page header strip */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 md:py-5">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{icon}</span>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg md:text-xl font-extrabold tracking-tight leading-tight text-gray-900">{PAGES[pageKey].label}</h1>
              <p className="text-gray-500 text-xs">Last updated {lastUpdated}</p>
            </div>
            <Link to="/" className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-primary transition-colors">
              ← Home
            </Link>
          </div>
        </div>
      </div>

      {/* Ambient blobs */}
      <div className="relative">
        <div className="pointer-events-none absolute -top-20 -left-20 w-80 h-80 bg-primary rounded-full blur-3xl" style={{ animation: 'pulseBlob 10s ease-in-out infinite' }} />
        <div className="pointer-events-none absolute top-40 -right-20 w-96 h-96 bg-orange-200 rounded-full blur-3xl" style={{ animation: 'pulseBlob 12s ease-in-out infinite 1s' }} />

        <div className="relative max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="grid lg:grid-cols-[260px_1fr] gap-8">
            {/* Sidebar: page nav + TOC */}
            <aside className="lg:sticky lg:top-24 lg:self-start space-y-4" style={{ animation: 'fadeSlide 0.6s ease-out' }}>
              {/* Document switcher */}
              <nav className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Legal Documents
                </div>
                <div className="p-2 space-y-1">
                  {(Object.keys(PAGES) as Array<keyof typeof PAGES>).map((key) => {
                    const p = PAGES[key];
                    const active = key === pageKey;
                    return (
                      <Link
                        key={key}
                        to={p.path}
                        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                          active ? 'bg-primary/10 text-primary ring-1 ring-primary/20' : 'text-gray-600 hover:bg-gray-50 hover:text-primary'
                        }`}
                      >
                        <span className="text-base">{p.icon}</span>
                        <span className="flex-1">{p.label}</span>
                        {active && <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />}
                      </Link>
                    );
                  })}
                </div>
              </nav>

              {/* Table of contents */}
              {sections.length > 0 && (
                <nav className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden hidden lg:block">
                  <div className="px-4 py-3 border-b border-gray-100 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    On This Page
                  </div>
                  <div className="p-2 space-y-0.5 max-h-[50vh] overflow-y-auto">
                    {sections.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => scrollToSection(s.id)}
                        className={`w-full text-left px-3 py-1.5 rounded-lg text-xs font-medium transition-all truncate ${
                          activeSection === s.id ? 'bg-primary/5 text-primary font-bold border-l-2 border-primary' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </nav>
              )}

              {/* Support callout */}
              <div className="bg-gradient-to-br from-primary/5 to-orange-50 border border-primary/10 rounded-2xl p-4 hidden lg:block">
                <div className="text-xl mb-1">💬</div>
                <div className="text-sm font-bold text-gray-900 mb-1">Have questions?</div>
                <p className="text-xs text-gray-500 leading-relaxed mb-3">Our team replies within 24 hours.</p>
                <Link to="/contact" className="block w-full text-center px-3 py-2 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90 transition-colors">
                  Contact us
                </Link>
              </div>
            </aside>

            {/* Main content */}
            <main ref={contentRef} style={{ animation: 'fadeSlide 0.6s ease-out 0.1s backwards' }}>
              <article className="bg-white border border-gray-100 rounded-3xl shadow-sm p-6 md:p-10">
                {isLoading ? (
                  <div className="space-y-3">
                    <div className="h-8 w-2/3 bg-gray-100 rounded animate-pulse" />
                    <div className="h-4 w-1/3 bg-gray-100 rounded animate-pulse" />
                    <div className="pt-6 space-y-2">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="h-4 bg-gray-50 rounded animate-pulse" style={{ width: `${60 + Math.random() * 40}%` }} />
                      ))}
                    </div>
                  </div>
                ) : error ? (
                  <div className="text-center py-16">
                    <div className="text-5xl mb-4 opacity-40">⚠️</div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Couldn't load {fallbackTitle}</h2>
                    <p className="text-gray-500 mb-6">{error}</p>
                    <button
                      onClick={load}
                      className="px-6 py-2.5 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors"
                    >
                      Retry
                    </button>
                  </div>
                ) : data ? (
                  <>
                    {/* Hero tag + title */}
                    <div className={`inline-flex items-center gap-2 ${accentColor} rounded-full px-3 py-1 text-xs font-semibold border mb-5`}>
                      <span>{icon}</span> {PAGES[pageKey].label}
                    </div>
                    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight leading-tight mb-3">{data.title}</h2>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400 mb-8 pb-6 border-b border-gray-100">
                      <div className="flex items-center gap-1.5">
                        <span>🕓</span>
                        <span>Last updated <strong className="text-gray-600">{lastUpdated}</strong></span>
                      </div>
                      <span className="text-gray-200">·</span>
                      <div className="flex items-center gap-1.5">
                        <span>📖</span>
                        <span>{sections.length} {sections.length === 1 ? 'section' : 'sections'}</span>
                      </div>
                    </div>

                    {/* Markdown content with explicit styling */}
                    <div className="legal-content">
                      <ReactMarkdown
                        components={{
                          h1: ({ node, ...props }) => <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mt-8 mb-3 pb-2 border-b border-gray-100 first:mt-0" {...props} />,
                          h2: ({ node, ...props }) => {
                            const text = String(props.children);
                            const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                            return <h2 id={id} className="text-xl md:text-2xl font-bold text-gray-900 mt-10 mb-3 pb-2 border-b border-gray-100 scroll-mt-24" {...props} />;
                          },
                          h3: ({ node, ...props }) => <h3 className="text-lg font-bold text-gray-900 mt-6 mb-2" {...props} />,
                          h4: ({ node, ...props }) => <h4 className="text-base font-bold text-gray-800 mt-4 mb-1.5" {...props} />,
                          p: ({ node, ...props }) => <p className="text-gray-600 leading-relaxed mb-4" {...props} />,
                          ul: ({ node, ...props }) => <ul className="list-disc ml-6 mb-4 space-y-1.5 text-gray-600 marker:text-primary" {...props} />,
                          ol: ({ node, ...props }) => <ol className="list-decimal ml-6 mb-4 space-y-1.5 text-gray-600 marker:text-primary marker:font-bold" {...props} />,
                          li: ({ node, ...props }) => <li className="leading-relaxed pl-1" {...props} />,
                          a: ({ node, ...props }) => <a className="text-primary font-semibold hover:underline" {...props} />,
                          strong: ({ node, ...props }) => <strong className="font-bold text-gray-800" {...props} />,
                          em: ({ node, ...props }) => <em className="italic" {...props} />,
                          blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-primary/30 pl-4 py-1 my-4 text-gray-500 italic bg-gray-50 rounded-r-lg" {...props} />,
                          code: ({ node, ...props }) => <code className="bg-gray-100 text-primary px-1.5 py-0.5 rounded text-sm font-mono" {...props} />,
                          hr: () => <hr className="my-6 border-gray-100" />,
                        }}
                      >
                        {data.content}
                      </ReactMarkdown>
                    </div>

                    {/* Footer */}
                    <div className="mt-10 pt-6 border-t border-gray-100 flex flex-wrap items-center justify-between gap-4">
                      <div className="text-xs text-gray-400">
                        Questions? Contact <a href="mailto:support@eatmeatapp.com" className="text-primary font-semibold hover:underline">support@eatmeatapp.com</a>
                      </div>
                      <button
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className="text-xs font-semibold text-gray-500 hover:text-primary transition-colors flex items-center gap-1"
                      >
                        ↑ Back to top
                      </button>
                    </div>
                  </>
                ) : null}
              </article>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
